import { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ShieldBan } from "lucide-react";
import { onUserBlocked } from "@/services/sessionEvents";

const DEFAULT_MESSAGE = "Your account has been blocked by an administrator.";
const AUTO_LOGOUT_MS = 5000;

const UserBlockedDialog = ({ handleLogout }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const timerRef = useRef(null);
  const didLogoutRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const logoutNow = useCallback(async () => {
    if (didLogoutRef.current) return;
    didLogoutRef.current = true;
    clearTimer();
    await handleLogout?.();
  }, [clearTimer, handleLogout]);

  useEffect(() => {
    return onUserBlocked((event) => {
      const incomingMessage = event?.detail?.message;
      setMessage(incomingMessage || DEFAULT_MESSAGE);
      setOpen(true);
      didLogoutRef.current = false;
      clearTimer();
      timerRef.current = window.setTimeout(() => {
        logoutNow();
      }, AUTO_LOGOUT_MS);
    });
  }, [clearTimer, logoutNow]);

  useEffect(() => {
    return () => clearTimer();
  }, [clearTimer]);

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        showCloseButton={false}
        className="sm:max-w-sm w-[calc(100%-2rem)] rounded-xl p-0 overflow-hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="items-center text-center gap-3 px-6 pt-6">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-50 dark:bg-red-950">
            <ShieldBan className="w-6 h-6 text-red-500 dark:text-red-400" />
          </div>
          <DialogTitle className="text-base font-semibold text-slate-900 dark:text-slate-100">
            Account Blocked
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
            {message}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="px-6 pb-6 pt-4">
          <Button
            onClick={logoutNow}
            className="w-full cursor-pointer font-medium text-white bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600"
          >
            Log Out
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UserBlockedDialog;
